import { useEffect, useState } from "react"
import { CombinedGraphQLErrors } from "@apollo/client/errors"
import { apolloClient } from "../../helpers/apollo"
import { encodeId } from "../../helpers/encode-id"
import { lookupUser } from "../../graphql/core/queries/lookup-user"
import { LookupUserQuery, LookupUserQueryVariables, Platforms } from "../../graphql/core-types"
import { trackEvent } from "../../helpers/logger"

// long enough that pasting or typing an id doesn't fire a request per keystroke
const LOOKUP_DEBOUNCE_MS = 400

export type TwitchChannel = {
  channel: string
  // their channel page
  url: string
  // twitch.tv/<channel>, for display
  label: string
  isLive: boolean
  // the live hls feed, set by pl-connect when the stream goes live
  streamUrl: string | null
  imageUrl: string | null
}

export type UserLookup =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'found', username: string | null, twitch: TwitchChannel | null }
  | { status: 'not-found' }
  // the backend couldn't be reached, so we can't say either way and shouldn't block the link
  | { status: 'unavailable' }

type LookupUser = LookupUserQuery['user']

// twitch logins are 4-25 letters, digits or underscores
const TWITCH_LOGIN_PATTERN = /^[a-z0-9_]{4,25}$/

// pl-connect fills twitchStreamLink with streamlink's hls playlist url when the stream goes live and clears it when that
// fails, so it's a playable video feed rather than a page link. it's signed and can expire, the preview falls back if so
const getStreamUrl = (link: string | null | undefined): string | null => {
  try {
    const url = new URL(link ?? '')
    return url.protocol === 'https:' && url.pathname.endsWith('.m3u8') ? url.toString() : null
  } catch {
    return null
  }
}

const getTwitchChannel = (user: LookupUser): TwitchChannel | null => {
  const twitch = user.platformIdentifiers?.find((identifier) => identifier?.platform === Platforms.TWITCH)
  // pl-connect builds www.twitch.tv/<gamer tag> from it, so the gamer tag is the channel login
  const channel = twitch?.gamerTag?.trim().toLowerCase()
  if (!twitch || !channel || !TWITCH_LOGIN_PATTERN.test(channel)) return null
  const isLive = Boolean(twitch.metadata?.twitchStreamLiveStatus)
  return {
    channel,
    url: `https://www.twitch.tv/${channel}`,
    label: `twitch.tv/${channel}`,
    isLive,
    // tried whenever it's there, the live flag can lag the webhook. pl-connect clears the link when the stream ends,
    // and a stale one just falls back to the stand in
    streamUrl: getStreamUrl(twitch.metadata?.twitchStreamLink),
    imageUrl: twitch.metadata?.twitchProfile?.profileImageUrl ?? null,
  }
}

// a well formed uuid can still belong to no one, the overlay then just renders nothing on stream
export const useUserLookup = (userId: string | null): UserLookup => {
  const [lookup, setLookup] = useState<UserLookup>({ status: 'idle' })

  useEffect(() => {
    if (!userId) {
      setLookup({ status: 'idle' })
      return
    }
    setLookup({ status: 'checking' })
    let isCancelled = false
    const timeout = setTimeout(async () => {
      let next: UserLookup
      try {
        const { data, error } = await apolloClient.query<LookupUserQuery, LookupUserQueryVariables>({
          query: lookupUser,
          variables: { id: encodeId('User', userId) },
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        })
        if (data?.user?.id) next = { status: 'found', username: data.user.loungeUsername ?? null, twitch: getTwitchChannel(data.user) }
        // the api answers an unknown id with a not_found graphql error and no data
        else if (!error || CombinedGraphQLErrors.is(error)) next = { status: 'not-found' }
        else next = { status: 'unavailable' }
      } catch {
        next = { status: 'unavailable' }
      }
      if (isCancelled) return
      setLookup(next)
      if (next.status === 'not-found') trackEvent('url_builder_user_not_found')
    }, LOOKUP_DEBOUNCE_MS)
    return () => {
      isCancelled = true
      clearTimeout(timeout)
    }
  }, [userId])

  return lookup
}
